import {Link} from '@shopify/hydrogen';
import {useMemo} from 'react';
import {HeaderEntity} from '~/types';
import {PageSectionContainer} from './index';
import {useHeaderContext} from '~/components/contexts/HeaderProvider.client';
import {DATA_TEST_IDS} from '~/lib/const';

export function Breadcrumb(props: {
  className?: string;
  pathname: string;
  // Here we pass the collection's and product's title
  title: string;
  // In the collection's page does not need to pass this array
  // In the product's page we need this array of collections
  // that the product belongs to.
  collections?: Array<{
    title: string;
    handle: string;
  }>;
}) {
  const header = useHeaderContext();
  const breadcrumb = useMemo(
    () =>
      generateBreadcrumb(
        props.collections ?? [{title: props.title}],
        header,
        props.pathname,
      ),
    [props.title, header, props.collections, props.pathname],
  );

  if (!breadcrumb) {
    return null;
  }

  return (
    <PageSectionContainer className={props.className} disableMarginY>
      <div data-testid={DATA_TEST_IDS.BREADCRUMB}>
        <span className="font-header text-13/18 lg:text-14/19">
          {breadcrumb.map((item) => (
            <span
              data-testid={DATA_TEST_IDS.BREADCRUMB_ITEM}
              key={item.url + item.title}
              className="font-extrabold after:content-['/'] after:px-[5px] last:after:content-['']"
            >
              {item.url ? (
                <Link to={item.url} className="hover:underline">
                  {item.title.toUpperCase()}
                </Link>
              ) : (
                <span>{item.title.toUpperCase()}</span>
              )}
            </span>
          ))}
        </span>
      </div>
    </PageSectionContainer>
  );
}

function generateBreadcrumb(
  titles: Array<{
    title: string;
    handle?: string;
  }>,
  header: HeaderEntity | undefined,
  pathname?: string,
):
  | Array<{
      url?: string;
      title: string;
    }>
  | undefined {
  for (const menu of header?.menuLinks ?? []) {
    if (!menu) {
      continue;
    }

    for (const column of menu?.megaMenuLinks?.columns ?? []) {
      if (!column) {
        continue;
      }

      for (const group of column?.groups ?? []) {
        if (!group) {
          continue;
        }

        for (const item of group?.items ?? []) {
          if (!item) {
            continue;
          }

          for (const subItem of item?.items ?? []) {
            if (!subItem) {
              continue;
            }

            const collection = titles?.find((collection) =>
              compare(collection?.title, subItem.title),
            );

            if (compare(subItem.title, collection?.title)) {
              return [
                {url: menu.href ?? '#', title: menu.text?.text ?? ''},
                // {
                //   url: group.titleLink ?? '#',
                //   title: group?.title ?? '',
                // },
                {
                  url: collection?.handle
                    ? `/collections/${collection.handle}`
                    : '#',
                  title: subItem?.title ?? '',
                },
              ];
            }
          }
        }

        const collection = titles.find((collection) =>
          compare(collection?.title, group.title),
        );

        if (compare(group.title, collection?.title)) {
          return [
            {url: menu?.href ?? '#', title: menu?.text?.text ?? ''},
            {
              url: pathname !== group?.titleLink ? group?.titleLink : '#',
              title: group?.title ?? '',
            },
          ];
        }
      }
    }

    const collection = titles?.find((collection) =>
      compare(collection?.title, menu.text?.text),
    );

    if (compare(menu.text?.text, collection?.title)) {
      return [{title: menu.text?.text ?? ''}];
    }
  }

  return undefined;
}

function compare(a?: string, b?: string) {
  return a?.toLowerCase() === b?.toLowerCase();
}
